import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Link,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  useBlueprintCommunityStatusQuery,
  useBlueprintModerationQueueQuery,
  useBlueprintModerationCaseQuery,
  useBlueprintModerateMutation,
  useBlueprintModerationAssetMutation,
} from '@/generated/graphql';

const actions = [
  [
    'withhold',
    'Withhold while reviewing',
    'Stops new downloads and previews for this blueprint. Keeps the case open.',
  ],
  [
    'removeAndBlock',
    'Confirm infringement & blacklist',
    'Removes this blueprint, blocks matching file uploads, and blacklists its creator from community sharing. All of their uploads become unavailable.',
  ],
  [
    'dismiss',
    'Close report without changing access',
    'Marks reports reviewed. Existing blueprint and account restrictions remain in place.',
  ],
  [
    'restore',
    'Restore this blueprint',
    'Clears this blueprint’s restriction. Required sharing consent and any account blacklist still apply.',
  ],
  [
    'unblock',
    'Lift creator’s blacklist',
    'Allows community sharing again. Individually removed or withheld blueprints remain unavailable.',
  ],
];

export default function AdminPage() {
  const access = useBlueprintCommunityStatusQuery({
    fetchPolicy: 'network-only',
  });
  const allowed = access.data?.blueprintCommunityStatus.canModerate === true;
  const [reviewed, setReviewed] = useState(false);
  const [offset, setOffset] = useState(0);
  const [lookup, setLookup] = useState('');
  const [blueprintId, setBlueprintId] = useState('');
  const [action, setAction] = useState('withhold');
  const [reason, setReason] = useState('');
  const [blockedKinds, setBlockedKinds] = useState<string[]>([]);
  const [confirm, setConfirm] = useState(false);
  const [message, setMessage] = useState('');
  const [assetUrl, setAssetUrl] = useState('');
  const queue = useBlueprintModerationQueueQuery({
    variables: { reviewed, offset },
    skip: !allowed,
    fetchPolicy: 'network-only',
  });
  const detail = useBlueprintModerationCaseQuery({
    variables: { id: blueprintId },
    skip: !allowed || !blueprintId,
    fetchPolicy: 'network-only',
  });
  const [moderate, saving] = useBlueprintModerateMutation();
  const [getAsset, assetState] = useBlueprintModerationAssetMutation();
  const selected = detail.data?.blueprintModerationCase;
  const choice = actions.find(([id]) => id === action)!;
  const choose = (id: string) => {
    setBlueprintId(id);
    setBlockedKinds([]);
    setReason('');
    setMessage('');
    setAssetUrl('');
  };
  const error =
    access.error ||
    queue.error ||
    detail.error ||
    saving.error ||
    assetState.error;
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1">
        Community moderation
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
        Review evidence, explain decisions, and give creators a way to appeal.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}
      {access.loading ? (
        <Typography>Checking access…</Typography>
      ) : !allowed ? (
        <Alert severity="info">
          This page is available to authorized moderators.
        </Alert>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            Reports are allegations, not proof. Follow the{' '}
            <Link
              href="/community-guidelines#copyright"
              target="_blank"
              rel="noreferrer"
            >
              notice and appeal process
            </Link>{' '}
            for copyright notices. Record the evidence and policy or legal
            basis, then notify affected parties through the monitored support
            channel. Keep complainants’ private contact details out of reasons
            shown to creators.
          </Alert>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 0 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="h6">Reports & appeals</Typography>
                <Button size="small" onClick={() => void queue.refetch()}>
                  Refresh
                </Button>
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={reviewed}
                    onChange={(_, value) => {
                      setReviewed(value);
                      setOffset(0);
                    }}
                  />
                }
                label="Show reviewed"
              />
              {queue.loading && <Typography>Loading reports…</Typography>}
              {!queue.loading &&
                !queue.data?.blueprintModerationQueue.length && (
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No reports in this view.
                  </Typography>
                )}
              <Stack spacing={1}>
                {queue.data?.blueprintModerationQueue.map((report) => (
                  <Box
                    key={report._id}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor:
                        blueprintId === report.blueprintId
                          ? 'primary.main'
                          : 'divider',
                      borderRadius: 2,
                    }}
                  >
                    <Chip
                      size="small"
                      label={
                        report.kind === 'appeal'
                          ? 'Creator appeal'
                          : report.kind === 'notice'
                            ? 'Moderator review'
                            : 'Community report'
                      }
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        my: 1,
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {report.reason}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {new Date(report.createdAt).toLocaleString()} ·{' '}
                      {report.status}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Reporter ID: {report.userId}
                    </Typography>
                    <Button
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={() => choose(report.blueprintId)}
                    >
                      Review blueprint
                    </Button>
                  </Box>
                ))}
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mt: 2 }}
              >
                <Button
                  disabled={!offset || queue.loading}
                  onClick={() => setOffset(Math.max(0, offset - 30))}
                >
                  Previous
                </Button>
                <Button
                  disabled={
                    queue.loading ||
                    (queue.data?.blueprintModerationQueue.length || 0) < 30
                  }
                  onClick={() => setOffset(offset + 30)}
                >
                  Next
                </Button>
              </Stack>
            </Paper>
            <Paper variant="outlined" sx={{ p: 3, flex: 1.3, minWidth: 0 }}>
              <Typography variant="h6">Inspect a blueprint</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose a report or enter an ID from a copyright notice.
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Blueprint ID"
                  size="small"
                  fullWidth
                  value={lookup}
                  onChange={(e) => setLookup(e.target.value)}
                />
                <Button
                  variant="outlined"
                  disabled={!lookup.trim()}
                  onClick={() => choose(lookup.trim())}
                >
                  Open
                </Button>
              </Stack>
              {detail.loading && (
                <Typography sx={{ mt: 2 }}>Loading blueprint…</Typography>
              )}
              {selected && !detail.loading && (
                <Stack spacing={2} sx={{ mt: 3 }}>
                  <Box>
                    <Typography variant="h5">{selected.title}</Typography>
                    <Typography color="text.secondary">
                      by {selected.creatorName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Creator ID: {selected.creatorId}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Chip label={selected.state} />
                    <Chip
                      label={
                        selected.ownerBlocked
                          ? 'Creator blacklisted'
                          : 'Creator can share'
                      }
                      color={selected.ownerBlocked ? 'warning' : 'default'}
                    />
                  </Stack>
                  <Typography variant="body2">
                    Publication: {selected.status}. {selected.reason}{' '}
                    {selected.ownerBlocked && selected.restrictionReason}
                  </Typography>
                  {selected.assets.map((file) => (
                    <Box key={file.kind}>
                      <Button
                        size="small"
                        disabled={assetState.loading}
                        onClick={async () => {
                          setAssetUrl('');
                          try {
                            const result = await getAsset({
                              variables: { id: selected.id, kind: file.kind },
                            });
                            setAssetUrl(
                              result.data?.blueprintModerationAsset || ''
                            );
                          } catch {
                            /* Mutation error shown above. */
                          }
                        }}
                      >
                        Get {file.kind} review link
                      </Button>
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ overflowWrap: 'anywhere' }}
                      >
                        {file.fileName} · SHA-256 {file.fullHash}{' '}
                        {file.blocked && '· Exact copies blocked'}
                      </Typography>
                    </Box>
                  ))}
                  {assetUrl && (
                    <Link href={assetUrl} target="_blank" rel="noreferrer">
                      Open review file (expires in 5 minutes)
                    </Link>
                  )}
                  <Divider />
                  <TextField
                    select
                    label="Action"
                    value={action}
                    onChange={(e) => setAction(e.target.value)}
                  >
                    {actions.map(([id, label]) => (
                      <MenuItem key={id} value={id}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Typography variant="body2" color="text.secondary">
                    {choice[2]}
                  </Typography>
                  {action === 'removeAndBlock' && (
                    <Box>
                      <Typography variant="body2">
                        Identify the infringing files. Only these exact files
                        will be blocked from reupload.
                      </Typography>
                      {selected.assets.map((file) => (
                        <FormControlLabel
                          key={file.kind}
                          control={
                            <Checkbox
                              checked={blockedKinds.includes(file.kind)}
                              onChange={(_, checked) =>
                                setBlockedKinds((values) =>
                                  checked
                                    ? [...values, file.kind]
                                    : values.filter(
                                        (kind) => kind !== file.kind
                                      )
                                )
                              }
                            />
                          }
                          label={`${file.kind}: ${file.fileName}`}
                        />
                      ))}
                    </Box>
                  )}
                  <TextField
                    label="Decision reason and evidence"
                    multiline
                    minRows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    inputProps={{ maxLength: 2000 }}
                    helperText="Explain the facts and policy or legal basis. Restriction reasons are visible to the creator."
                  />
                  <Button
                    variant="contained"
                    color={action === 'removeAndBlock' ? 'error' : 'primary'}
                    disabled={
                      !reason.trim() ||
                      saving.loading ||
                      (action === 'removeAndBlock' && !blockedKinds.length)
                    }
                    onClick={() => setConfirm(true)}
                  >
                    Review decision
                  </Button>
                  {message && <Alert severity="success">{message}</Alert>}
                  <Typography variant="subtitle2">Decision history</Typography>
                  {!selected.history.length && (
                    <Typography variant="body2" color="text.secondary">
                      No decisions recorded.
                    </Typography>
                  )}
                  {selected.history
                    .slice()
                    .reverse()
                    .map((event, i) => (
                      <Box key={`${event.createdAt}-${i}`}>
                        <Typography variant="body2" fontWeight={600}>
                          {actions.find(([id]) => id === event.action)?.[1] ||
                            event.action}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap' }}
                        >
                          {event.reason}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(event.createdAt).toLocaleString()} ·
                          Moderator {event.actorId}
                        </Typography>
                      </Box>
                    ))}
                </Stack>
              )}
            </Paper>
          </Stack>
          <Dialog
            open={confirm}
            onClose={saving.loading ? undefined : () => setConfirm(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>{choice[1]}</DialogTitle>
            <DialogContent>
              <Typography>{selected?.title}</Typography>
              <Typography sx={{ my: 2 }}>{choice[2]}</Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{reason}</Typography>
              {action === 'removeAndBlock' && (
                <Typography sx={{ mt: 2 }}>
                  Files to block: {blockedKinds.join(', ')}
                </Typography>
              )}
              {saving.error && (
                <Alert severity="error">{saving.error.message}</Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                disabled={saving.loading}
                onClick={() => setConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                disabled={saving.loading}
                color={action === 'removeAndBlock' ? 'error' : 'primary'}
                variant="contained"
                onClick={async () => {
                  try {
                    await moderate({
                      variables: {
                        id: blueprintId,
                        action,
                        reason,
                        blockedKinds,
                      },
                    });
                    setConfirm(false);
                    setMessage(
                      'Decision recorded. Notify affected parties through the support channel.'
                    );
                    setAssetUrl('');
                    await Promise.all([queue.refetch(), detail.refetch()]);
                  } catch {
                    /* Preserve the form and show the error. */
                  }
                }}
              >
                Confirm decision
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Container>
  );
}
